import { ApiResponse, PayloadProps } from "../services/schema/schema";
import axios from "../axios/http";
//const apiBaseUrl = process.env.REACT_APP_API_URL;
// IIDM-3064 T1: the module-scope `export const token` that lived here was read ONCE at module
// load and went stale on every login. Read the token per call from `auth/token-store` instead;
// the axios request interceptor (axios/http.ts) attaches it to every request.

/*  eslint "require-jsdoc": ["error", {
      "require": {
          "FunctionDeclaration": true,
          "ArrowFunctionExpression": true,
          "FunctionExpression": true
    }
}]  */
/**
 * IIDM-2884 — the ONE definition of what a failed call looks like.
 *
 * It is deliberately a well-formed `ApiResponse`, not a throw and not `undefined`. 64 call sites
 * annotate the resolved value as `ApiResponse` and there are 195 reads of `response.result`;
 * ZERO read a raw axios shape. So a caller's existing `if (response.result.success)` takes its
 * failure branch on its own, and not one of the 95 call sites needs editing.
 *
 * Rethrowing was the obvious alternative and is worse here: only 37 of the 95 sites have a
 * `.catch`, so it would convert a silent bug into 58 unhandled rejections.
 *
 * @param {string} message a safe, human-readable message
 * @param {number} statusCode the HTTP status, or 0 when the request never opened
 * @return {ApiResponse} the failure, in the shape every caller already expects
 */
export function failureResponse(message: string, statusCode = 0): ApiResponse {
  return {
    result: {
      data: null,
      message,
      status_code: statusCode,
      success: false,
    },
  };
}

/**
 * @param {PayloadProps} params required params
 * @return {object} params
 */
export async function triggerApi(params: PayloadProps): Promise<ApiResponse> {
  return getResponse(params);
}
/**
 * @param {object} params required params
 * @return {object} params
 */
async function getResponse(params: any): Promise<ApiResponse> {
  // const bodyFormData = new FormData();
  // for (const key in params.payload) {
  //   bodyFormData.append(key, params.payload[key]);
  // }
  //params.payload['timezone']= new Date().getTimezoneOffset()
  params.headers = {
    timezoneoffset: new Date().getTimezoneOffset(),
  };
  const methodTypes = ["GET", "POST", "PUT", "DELETE", "PATCH"];
  if (!(methodTypes.indexOf(params.method.toUpperCase()) !== -1)) {
    params.method = "GET";
  }
  try {
    const res = await axios({
      method: params.method,
      // IIDM-2883: pass the endpoint as a RELATIVE url and let the axios instance
      // join it to its own baseURL (src/axios/http.ts). Concatenating the env var
      // by hand produced an invalid URL whenever REACT_APP_API_URL had no trailing
      // slash and the endpoint had no leading one — 207 of the 220 entries in
      // endPoints.tsx have no leading slash — e.g.
      //   "http://host:8086" + "v1/State-Customer" => "http://host:8086v1/..."
      // whose port parses as "8086v1", so XMLHttpRequest.open() threw
      // SyntaxError: Invalid URL and the request was never issued at all.
      // axios combineURLs() strips a trailing slash from the base and a leading
      // one from the path, so this is correct for both conventions.
      url: params.apiUrl,
      data: params.payload,
      headers: params.headers ? params.headers : {},
    });
    //console.log('api', res);
    //console.log('baseApi11111', apiBaseUrl);
    const response = res && typeof res === "object" ? res.data : undefined;

    // IIDM-2884 — a RESOLVED value can still be a failure. The response interceptor used to
    // resolve with the string "apierror" on 500/404 and with `undefined` on its fall-through, so
    // `res.data` was `undefined` and the caller's `response.result` threw a TypeError. The
    // interceptor no longer does either, and this is the belt to that braces: whatever resolves,
    // the caller gets the contract.
    if (response === undefined || response === null) {
      console.log("triggerApi: request resolved with no body", params.apiUrl);

      return failureResponse("Something went wrong. Please try again.");
    }

    return response as unknown as ApiResponse;
  } catch (err: any) {
    // IIDM-2884 — log the ERROR ITSELF, not `err.response`. A request that never opened (invalid
    // URL, refused connection, blocked, timeout) has no `.response`, so the old line printed a
    // bare `undefined` and told you nothing. That is the exact fault that made IIDM-2883 take
    // several sessions to diagnose.
    console.error("triggerApi failed", params.apiUrl, err);

    const status = err && err.response && err.response.status ? err.response.status : 0;
    const serverMessage =
      err && err.response && err.response.data && err.response.data.result
        ? err.response.data.result.message
        : undefined;

    return failureResponse(
      serverMessage || "Something went wrong. Please try again.",
      status
    );
  }
}
